import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Mail, Sparkles } from "lucide-react";
import { getAttribution, pushEvent } from "@/lib/tracking";

declare global {
  interface Window {
    oaiq?: (...args: unknown[]) => void;
  }
}

type Status = "idle" | "sending" | "ok" | "error";
type Errors = Partial<Record<string, string>>;

const PROJECT_TYPES = [
  "Pharma, Biotech & Life Science",
  "Fødevareproduktion",
  "Datacentre",
  "Hospitaler & Sundhedsbyggeri",
  "Kontorer & Hovedsæder",
  "Energi, Forsyning & Infrastruktur",
  "Transport & Logistik",
  "Andet",
];

const PROJECT_VALUES = [
  "0-100 mio. kr.",
  "100-500 mio. kr.",
  "500-1000 mio. kr.",
  "1000+ mio. kr.",
];

const CHALLENGES = [
  "Uklar ansvarsfordeling",
  "Manglende koordinering mellem entreprenører",
  "Utilstrækkelig planlægning af arbejdet",
  "Mangelfuld opfølgning og tilsyn",
  "Ændringer uden opdateret risikovurdering",
  "Grænseflader mellem kontrakter",
  "Andet",
];

const FORM_ID = "free_hse_site_check";
const TOTAL_STEPS = 5;

const fieldClass =
  "mt-2 w-full min-h-[48px] rounded-xl border border-foreground/15 bg-background/60 px-4 py-3 text-base text-foreground placeholder:text-foreground/40 outline-none transition-colors focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/40";
const labelClass = "block text-sm font-semibold text-foreground";

type Data = {
  type: string;
  typeAndet: string;
  projektnavn: string;
  lokation: string;
  vaerdi: string;
  startdato: string;
  slutdato: string;
  amo: string;
  ulykker: string;
  udfordringer: string[];
  besked: string;
  virksomhed: string;
  cvr: string;
  navn: string;
  titel: string;
  telefon: string;
  email: string;
  samtykke: boolean;
};

const INITIAL: Data = {
  type: "",
  typeAndet: "",
  projektnavn: "",
  lokation: "",
  vaerdi: PROJECT_VALUES[0],
  startdato: "",
  slutdato: "",
  amo: "",
  ulykker: "",
  udfordringer: [],
  besked: "",
  virksomhed: "",
  cvr: "",
  navn: "",
  titel: "",
  telefon: "",
  email: "",
  samtykke: true,
};

function validateStep(step: number, d: Data): Errors {
  const e: Errors = {};
  if (step === 1) {
    if (!d.type) e.type = "Vælg, hvad projektet omhandler.";
    else if (d.type === "Andet" && !d.typeAndet.trim())
      e.typeAndet = "Uddyb venligst, hvad projektet omhandler.";
  }
  if (step === 2) {
    if (!d.projektnavn.trim()) e.projektnavn = "Udfyld projekttitel.";
    if (!d.lokation.trim()) e.lokation = "Angiv projektets adresse eller by.";
    if (!d.vaerdi) e.vaerdi = "Vælg projektets samlede entreprise-/projektværdi.";
  }
  if (step === 3) {
    if (!d.amo) e.amo = "Vælg ja eller nej.";
    if (!d.ulykker) e.ulykker = "Vælg ja eller nej.";
  }
  if (step === 4 && d.udfordringer.length === 0)
    e.udfordringer = "Vælg mindst én sikkerhedsudfordring.";
  if (step === 5) {
    if (!d.virksomhed.trim()) e.virksomhed = "Udfyld virksomhedsnavn.";
    if (!d.cvr.trim()) e.cvr = "Udfyld CVR-nummer.";
    if (!d.navn.trim()) e.navn = "Udfyld kontaktperson.";
    if (!d.titel.trim()) e.titel = "Udfyld titel/rolle.";
    if (!d.email.trim()) e.email = "Udfyld jeres arbejdsmail.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim()))
      e.email = "Indtast en gyldig mailadresse.";
    if (d.telefon.trim() && d.telefon.replace(/\D/g, "").length < 8)
      e.telefon = "Indtast et gyldigt telefonnummer.";
    if (!d.samtykke) e.samtykke = "Du skal give samtykke for at sende ansøgningen.";
  }
  return e;
}

export function SikkerhedstjekForm() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState<Data>(INITIAL);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<Status>("idle");
  const [serverError, setServerError] = useState("");
  const [honeypot, setHoneypot] = useState("");

  const pct = useMemo(() => Math.round((step / TOTAL_STEPS) * 100), [step]);
  const set = <K extends keyof Data>(key: K, value: Data[K]) =>
    setData((d) => ({ ...d, [key]: value }));

  const toggleChallenge = (c: string) =>
    setData((d) => ({
      ...d,
      udfordringer: d.udfordringer.includes(c)
        ? d.udfordringer.filter((x) => x !== c)
        : [...d.udfordringer, c],
    }));

  function next() {
    const found = validateStep(step, data);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    if (step < TOTAL_STEPS) setStep(step + 1);
  }

  function back() {
    setErrors({});
    if (step > 1) setStep(step - 1);
  }

  async function submit() {
    const found = validateStep(TOTAL_STEPS, data);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setStatus("sending");
    setServerError("");
    try {
      const res = await fetch("/api/sikkerhedstjek", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          navn: data.navn,
          email: data.email,
          telefon: data.telefon,
          lokation: data.lokation,
          type: data.type === "Andet" ? `Andet: ${data.typeAndet.trim()}` : data.type,
          besked: data.besked,
          virksomhed: data.virksomhed,
          cvr: data.cvr,
          titel: data.titel,
          projektnavn: data.projektnavn,
          vaerdi: data.vaerdi,
          startdato: data.startdato,
          slutdato: data.slutdato,
          amo: data.amo,
          ulykker: data.ulykker,
          udfordringer: data.udfordringer.join(", "),
          company_url: honeypot,
          attribution: getAttribution(),
        }),
      });
      const jsonRes = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!res.ok || !jsonRes.ok) throw new Error(jsonRes.error || "send_failed");

      pushEvent("lead_form_submit", {
        form_id: FORM_ID,
        page_path: typeof window !== "undefined" ? window.location.pathname : "",
      });
      if (typeof window !== "undefined" && typeof window.oaiq === "function") {
        window.oaiq("measure", "lead_created", { type: "customer_action" });
      }
      setStatus("ok");
    } catch {
      setStatus("error");
      setServerError(
        "Ansøgningen kunne ikke sendes lige nu. Prøv igen, eller skriv til info@misupport.dk.",
      );
    }
  }

  if (status === "ok") {
    return (
      <div
        role="status"
        className="rounded-2xl border border-primary/40 bg-primary/10 p-8 text-foreground"
      >
        <CheckCircle2 className="h-8 w-8 text-primary" aria-hidden="true" />
        <h3 className="mt-4 text-2xl font-bold">Tak for jeres ansøgning</h3>
        <p className="mt-3 leading-relaxed text-foreground/85">
          Vi gennemgår oplysningerne og kontakter jer, hvis projektet matcher kriterierne for det
          gratis sikkerhedstjek.
        </p>
      </div>
    );
  }

  const err = (name: string) =>
    errors[name] ? (
      <p id={`err-${name}`} className="mt-2 text-sm text-primary">
        {errors[name]}
      </p>
    ) : null;

  const aria = (name: string) => ({
    "aria-invalid": errors[name] ? true : undefined,
    "aria-describedby": errors[name] ? `err-${name}` : undefined,
  });

  const choiceClass = (active: boolean) =>
    `flex min-h-[56px] items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
      active
        ? "border-primary bg-primary/10"
        : "border-foreground/15 bg-background/50 hover:border-primary/50"
    }`;

  const marker = (active: boolean, round = false) => (
    <span
      className={`flex h-5 w-5 shrink-0 items-center justify-center border transition-colors ${
        round ? "rounded-full" : "rounded-md"
      } ${active ? "border-primary bg-primary" : "border-foreground/30"}`}
    >
      {active && <CheckCircle2 className="h-3.5 w-3.5 text-primary-foreground" aria-hidden="true" />}
    </span>
  );

  const yesNo = (name: "amo" | "ulykker", legend: string) => (
    <fieldset className="mt-8">
      <legend className={labelClass}>{legend}</legend>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {["Ja", "Nej"].map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => {
              set(name, v);
              setErrors((prev) => ({ ...prev, [name]: undefined }));
            }}
            aria-pressed={data[name] === v}
            className={choiceClass(data[name] === v)}
          >
            {marker(data[name] === v, true)}
            <span className="font-medium">{v}</span>
          </button>
        ))}
      </div>
      {err(name)}
    </fieldset>
  );

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (step === TOTAL_STEPS) void submit();
        else next();
      }}
    >
      <input
        type="text"
        name="company_url"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden="true"
      />

      {/* Progress */}
      <div className="flex items-center justify-between text-sm font-medium text-foreground/70">
        <span>
          Trin {step} af {TOTAL_STEPS}
        </span>
        <span>{pct}% udfyldt</span>
      </div>
      <div
        className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-foreground/10"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full rounded-full bg-primary transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="mt-8 min-h-[320px]">
        {step === 1 && (
          <fieldset>
            <legend className="text-2xl font-bold tracking-tight">
              Hvad omhandler jeres projekt?
            </legend>
            <p className="mt-2 text-sm text-foreground/70">
              Vælg det område, der passer bedst, så matcher vi jer med den rette HSE-specialist.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {PROJECT_TYPES.map((t) => {
                const active = data.type === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      set("type", t);
                      setErrors({});
                    }}
                    aria-pressed={active}
                    className={choiceClass(active)}
                  >
                    {marker(active)}
                    <span className="font-medium">{t}</span>
                  </button>
                );
              })}
            </div>
            {err("type")}
            {data.type === "Andet" && (
              <div className="mt-4">
                <label className={labelClass} htmlFor="st-type-andet">
                  Uddyb gerne, hvad projektet omhandler *
                </label>
                <input
                  id="st-type-andet"
                  maxLength={200}
                  value={data.typeAndet}
                  onChange={(e) => {
                    set("typeAndet", e.target.value);
                    setErrors((prev) => ({ ...prev, typeAndet: undefined }));
                  }}
                  placeholder="Fx boligbyggeri, lagerdistribution, uddannelsesinstitutioner …"
                  className={fieldClass}
                  {...aria("typeAndet")}
                />
                {err("typeAndet")}
              </div>
            )}
          </fieldset>
        )}

        {step === 2 && (
          <div>
            <h3 className="text-2xl font-bold tracking-tight">Projektoplysninger</h3>
            <p className="mt-2 text-sm text-foreground/70">
              Fortæl os kort om projektet – det hjælper os med at vurdere ansøgningen.
            </p>

            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor="st-projektnavn">
                  Projekttitel *
                </label>
                <input
                  id="st-projektnavn"
                  maxLength={200}
                  value={data.projektnavn}
                  onChange={(e) => {
                    set("projektnavn", e.target.value);
                    setErrors((prev) => ({ ...prev, projektnavn: undefined }));
                  }}
                  className={fieldClass}
                  {...aria("projektnavn")}
                />
                {err("projektnavn")}
              </div>
              <div>
                <label className={labelClass} htmlFor="st-lokation">
                  Projektlokation *
                </label>
                <input
                  id="st-lokation"
                  autoComplete="address-level2"
                  maxLength={300}
                  placeholder="Fx adresse eller by"
                  value={data.lokation}
                  onChange={(e) => set("lokation", e.target.value)}
                  className={fieldClass}
                  {...aria("lokation")}
                />
                {err("lokation")}
              </div>
            </div>

            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor="st-startdato">
                  Forventet startdato
                </label>
                <input
                  id="st-startdato"
                  type="date"
                  value={data.startdato}
                  onChange={(e) => set("startdato", e.target.value)}
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="st-slutdato">
                  Forventet slutdato
                </label>
                <input
                  id="st-slutdato"
                  type="date"
                  value={data.slutdato}
                  onChange={(e) => set("slutdato", e.target.value)}
                  className={fieldClass}
                />
              </div>
            </div>

            <fieldset className="mt-8">
              <legend className={labelClass}>
                Projektværdi (estimeret) *
              </legend>
              <p className="mt-1 text-sm text-foreground/70">
                Vælg det interval, der passer bedst (mio. DKK).
              </p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2" {...aria("vaerdi")}>
                {PROJECT_VALUES.map((v) => {
                  const active = data.vaerdi === v;
                  const short = v.replace(" mio. kr.", "");
                  return (
                    <button
                      key={v}
                      type="button"
                      aria-pressed={active}
                      onClick={() => {
                        set("vaerdi", v);
                        setErrors((prev) => ({ ...prev, vaerdi: undefined }));
                      }}
                      className={`rounded-xl border px-5 py-4 text-left transition ${
                        active
                          ? "border-primary bg-primary/10"
                          : "border-foreground/15 bg-background/60 hover:border-primary/50"
                      }`}
                    >
                      <span
                        className={`block text-xl font-extrabold tracking-tight ${
                          active ? "text-primary" : "text-foreground"
                        }`}
                      >
                        {short}
                      </span>
                      <span className="mt-1 block text-xs font-bold uppercase tracking-widest text-foreground/50">
                        mio. DKK
                      </span>
                    </button>
                  );
                })}
              </div>
              {err("vaerdi")}
            </fieldset>

          </div>
        )}

        {step === 3 && (
          <div>
            <h3 className="text-2xl font-bold tracking-tight">Sikkerhedsorganisation</h3>
            <p className="mt-2 text-sm text-foreground/70">
              To korte spørgsmål om projektets nuværende sikkerhedsarbejde.
            </p>
            {yesNo("amo", "Findes der en aktiv arbejdsmiljøorganisation på projektet? *")}
            {yesNo(
              "ulykker",
              "Har projektet haft registrerede ulykker eller nærved-hændelser de seneste 6 måneder? *",
            )}
          </div>
        )}

        {step === 4 && (
          <div>
            <fieldset>
              <legend className="text-2xl font-bold tracking-tight">
                Hvad ser I som de største sikkerhedsudfordringer?
              </legend>
              <p className="mt-2 text-sm text-foreground/70">Vælg gerne flere.</p>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {CHALLENGES.map((c) => {
                  const active = data.udfordringer.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => {
                        toggleChallenge(c);
                        setErrors((prev) => ({ ...prev, udfordringer: undefined }));
                      }}
                      aria-pressed={active}
                      className={choiceClass(active)}
                    >
                      {marker(active)}
                      <span className="font-medium">{c}</span>
                    </button>
                  );
                })}
              </div>
              {err("udfordringer")}
            </fieldset>

            <div className="mt-8">
              <label className={labelClass} htmlFor="st-besked">
                Uddyb yderligere
              </label>
              <textarea
                id="st-besked"
                rows={5}
                maxLength={5000}
                placeholder="Fx projektets fase, antal personer på pladsen og særlige risici"
                value={data.besked}
                onChange={(e) => set("besked", e.target.value)}
                className={fieldClass}
              />
            </div>
          </div>
        )}

        {step === 5 && (
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-primary">
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              Sidste trin
            </p>
            <h3 className="mt-3 text-2xl font-bold tracking-tight">Virksomhedsoplysninger</h3>
            <ul className="mt-5 space-y-2 text-sm text-foreground/80">
              {[
                "Gratis og uforpligtende gennemgang af jeres byggeplads",
                "Rapport med konkrete observationer og forbedringsforslag",
                "Vi vender tilbage inden for få hverdage",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-7 grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor="st-virksomhed">
                  Virksomhedsnavn *
                </label>
                <input
                  id="st-virksomhed"
                  autoComplete="organization"
                  maxLength={200}
                  value={data.virksomhed}
                  onChange={(e) => set("virksomhed", e.target.value)}
                  className={fieldClass}
                  {...aria("virksomhed")}
                />
                {err("virksomhed")}
              </div>
              <div>
                <label className={labelClass} htmlFor="st-cvr">
                  CVR-nummer *
                </label>
                <input
                  id="st-cvr"
                  inputMode="numeric"
                  maxLength={20}
                  value={data.cvr}
                  onChange={(e) => set("cvr", e.target.value)}
                  className={fieldClass}
                  {...aria("cvr")}
                />
                {err("cvr")}
              </div>
              <div>
                <label className={labelClass} htmlFor="st-navn">
                  Kontaktperson *
                </label>
                <input
                  id="st-navn"
                  autoComplete="name"
                  maxLength={200}
                  value={data.navn}
                  onChange={(e) => set("navn", e.target.value)}
                  className={fieldClass}
                  {...aria("navn")}
                />
                {err("navn")}
              </div>
              <div>
                <label className={labelClass} htmlFor="st-titel">
                  Titel / Rolle *
                </label>
                <input
                  id="st-titel"
                  autoComplete="organization-title"
                  maxLength={200}
                  value={data.titel}
                  onChange={(e) => set("titel", e.target.value)}
                  className={fieldClass}
                  {...aria("titel")}
                />
                {err("titel")}
              </div>
              <div>
                <label className={labelClass} htmlFor="st-telefon">
                   Telefon
                </label>
                <input
                  id="st-telefon"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  maxLength={60}
                  value={data.telefon}
                  onChange={(e) => set("telefon", e.target.value)}
                  className={fieldClass}
                  {...aria("telefon")}
                />
                {err("telefon")}
              </div>
              <div>
                <label className={labelClass} htmlFor="st-email">
                  E-mail *
                </label>
                <div className="relative">
                  <Mail
                    className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/40"
                    aria-hidden="true"
                  />
                  <input
                    id="st-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    maxLength={200}
                    placeholder="navn@virksomhed.dk"
                    value={data.email}
                    onChange={(e) => set("email", e.target.value)}
                    className={`${fieldClass} mt-0 pl-11`}
                    {...aria("email")}
                  />
                </div>
                {err("email")}
              </div>
            </div>

            <label className="mt-6 flex items-start gap-3 text-sm leading-relaxed text-foreground/80">
              <input
                type="checkbox"
                checked={data.samtykke}
                onChange={(e) => set("samtykke", e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                {...aria("samtykke")}
              />
              <span>
                Jeg giver samtykke til, at MI Support må kontakte os vedrørende vurdering af
                ansøgningen og opfølgning.
              </span>
            </label>
            {err("samtykke")}

            {status === "error" && (
              <p role="alert" className="mt-4 text-sm text-primary">
                {serverError}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="mt-8 flex items-center justify-between gap-4 border-t border-foreground/10 pt-6">
        <button
          type="button"
          onClick={back}
          disabled={step === 1}
          className="inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-foreground/80 transition-opacity hover:text-primary disabled:pointer-events-none disabled:opacity-40"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Tilbage
        </button>

        <button
          type="submit"
          disabled={status === "sending"}
          className="btn-cta min-h-[48px] justify-center disabled:opacity-70"
        >
          {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {step === TOTAL_STEPS
            ? status === "sending"
              ? "Sender ansøgning …"
              : "Send min ansøgning"
            : "Næste"}
          {step < TOTAL_STEPS && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
        </button>
      </div>

      <p className="mt-5 text-xs leading-relaxed text-foreground/55">
        MI Support tilbyder gratis sikkerhedstjek til udvalgte projekter. Vi gennemgår alle
        ansøgninger individuelt og prioriterer de projekter, hvor vores specialister kan skabe
        størst sikkerhedsmæssig værdi. Læs mere i vores{" "}
        <Link to="/persondatapolitik" className="underline hover:text-primary">
          persondatapolitik
        </Link>
        .
      </p>
    </form>
  );
}
